/**
 * Theme Panel - Color & Typography Editor
 *
 * Side panel for editing template theme colors and fonts.
 * Changes propagate to the template JSON in real-time.
 * Uses plain-language labels so designers know what each color affects.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Palette, Type, ChevronDown, ChevronRight, X, Check, Sparkles } from 'lucide-react';

const FONT_OPTIONS = [
  'Inter', 'Playfair Display', 'Montserrat', 'Roboto', 'Open Sans',
  'Lato', 'Poppins', 'Raleway', 'Oswald', 'Merriweather',
  'Nunito', 'Source Sans Pro', 'PT Sans', 'Noto Sans', 'Ubuntu',
  'Cormorant Garamond', 'Lora', 'Crimson Text', 'DM Sans', 'Work Sans',
  'Libre Baskerville', 'Josefin Sans', 'Quicksand', 'Abril Fatface', 'Cinzel'
];

// Plain language descriptions for each color role
const COLOR_DESCRIPTIONS = {
  primary: { label: 'Buttons & Headers', hint: 'Main brand color used for buttons, navigation bar, and dark section backgrounds' },
  secondary: { label: 'Secondary Accent', hint: 'Complementary color for badges, tags, and secondary buttons' },
  accent: { label: 'Highlights & Links', hint: 'Eye-catching color for links, icons, and special callouts' },
  background: { label: 'Page Background', hint: 'The main background color of your entire website' },
  text: { label: 'Body Text', hint: 'Color of paragraphs and general text content' },
  textSecondary: { label: 'Subtle Text', hint: 'Lighter text used for descriptions, captions, and labels' },
  textLight: { label: 'Muted Text', hint: 'Very light text for footnotes and placeholders' },
  surface: { label: 'Card Background', hint: 'Background color for cards, panels, and content boxes' },
  border: { label: 'Borders & Lines', hint: 'Color of dividers, card borders, and input outlines' },
};

const ColorInput = ({ label, hint, value, onChange, colorKey, onHover, onLeave }) => {
  const [showHint, setShowHint] = useState(false);

  return (
    <div
      className="py-2 px-1 rounded-lg hover:bg-gray-50 transition-colors duration-150"
      onMouseEnter={() => { setShowHint(true); onHover?.(colorKey); }}
      onMouseLeave={() => { setShowHint(false); onLeave?.(); }}
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="w-9 h-9 rounded-lg border-2 border-gray-200 cursor-pointer p-0.5 hover:border-blue-400 transition-colors"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-xs font-medium text-gray-700 block">{label}</label>
          {showHint && hint && (
            <p className="text-[10px] text-gray-400 mt-0.5 leading-tight animate-fadeIn">{hint}</p>
          )}
        </div>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-20 text-xs px-2 py-1.5 border border-gray-200 rounded-md font-mono text-gray-600 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none transition-all"
          placeholder="#000000"
        />
      </div>
    </div>
  );
};

const FontSelect = ({ label, hint, value, onChange }) => {
  useEffect(() => {
    const families = FONT_OPTIONS.map(f => f.replace(/\s+/g, '+')).join('&family=');
    const id = 'theme-panel-font-preview';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${families}:wght@400;700&display=swap`;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="py-2 px-1 rounded-lg hover:bg-gray-50 transition-colors duration-150">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
          <Type className="w-4 h-4 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-xs font-medium text-gray-700 block mb-1">{label}</label>
          {hint && <p className="text-[10px] text-gray-400 leading-tight">{hint}</p>}
        </div>
      </div>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-2 text-xs px-2.5 py-2 border border-gray-200 rounded-md bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none transition-all"
        style={{ fontFamily: value || 'inherit' }}
      >
        <option value="">Default</option>
        {FONT_OPTIONS.map(font => (
          <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>
        ))}
      </select>
      {value && (
        <div className="mt-2 px-3 py-2 bg-gray-50 rounded-md">
          <p className="text-sm" style={{ fontFamily: value }}>
            The quick brown fox jumps over the lazy dog
          </p>
        </div>
      )}
    </div>
  );
};

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="flex-1 text-left">{title}</span>
        {badge && (
          <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-500 rounded-full font-medium">{badge}</span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-3 pb-3">{children}</div>
      </div>
    </div>
  );
};

const ThemePanel = ({ theme, onChange, onClose }) => {
  const colors = theme?.colors || {};
  const typography = theme?.typography || {};
  const [showApplied, setShowApplied] = useState(false);
  const [highlightedColor, setHighlightedColor] = useState(null);

  const flashApplied = useCallback(() => {
    setShowApplied(true);
    setTimeout(() => setShowApplied(false), 1200);
  }, []);

  // Broadcast highlighted color role to the editor for visual feedback
  useEffect(() => {
    if (highlightedColor) {
      document.querySelector('.editable-renderer')?.setAttribute('data-highlight-color', highlightedColor);
    } else {
      document.querySelector('.editable-renderer')?.removeAttribute('data-highlight-color');
    }
  }, [highlightedColor]);

  const updateColor = useCallback((key, value) => {
    const newColors = { ...colors };

    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      if (typeof newColors[parent] === 'object' && newColors[parent] !== null && !Array.isArray(newColors[parent])) {
        newColors[parent] = { ...newColors[parent], [child]: value };
      } else {
        newColors[parent] = value;
      }
    } else {
      if (key === 'primary' && Array.isArray(newColors.primary)) {
        newColors.primary = [value, value];
      } else {
        newColors[key] = value;
      }
    }

    onChange({ ...theme, colors: newColors });
    flashApplied();
  }, [colors, theme, onChange, flashApplied]);

  const updateFont = useCallback((key, value) => {
    onChange({
      ...theme,
      typography: { ...typography, [key]: value }
    });
    flashApplied();
  }, [typography, theme, onChange, flashApplied]);

  // Extract flat color values
  const primaryColor = Array.isArray(colors.primary) ? colors.primary[0] : colors.primary;
  const textColor = typeof colors.text === 'object' ? colors.text.primary : colors.text;
  const textSecondary = typeof colors.text === 'object'
    ? colors.text.secondary
    : (colors.textSecondary || undefined);
  const textLight = typeof colors.text === 'object'
    ? colors.text.light
    : (colors.textLight || undefined);
  const bgColor = typeof colors.background === 'object' ? colors.background.primary : colors.background;
  const surfaceColor = typeof colors.surface === 'object' ? colors.surface.primary : colors.surface;
  const borderColor = typeof colors.border === 'object' ? colors.border.primary : colors.border;

  const accentColor = colors.accent || (() => {
    const palettes = ['amber', 'rose', 'orange', 'emerald', 'teal', 'blue', 'indigo', 'violet'];
    for (const pal of palettes) {
      if (colors[pal] && typeof colors[pal] === 'object') {
        return colors[pal]['900'] || colors[pal]['800'] || colors[pal]['700'];
      }
    }
    return undefined;
  })();

  const handleTextChange = useCallback((v) => {
    if (typeof colors.text === 'object') {
      updateColor('text.primary', v);
    } else {
      updateColor('text', v);
    }
  }, [colors.text, updateColor]);

  const handleTextSecondaryChange = useCallback((v) => {
    if (typeof colors.text === 'object') {
      updateColor('text.secondary', v);
    } else {
      updateColor('textSecondary', v);
    }
  }, [colors.text, updateColor]);

  const handleTextLightChange = useCallback((v) => {
    if (typeof colors.text === 'object') {
      updateColor('text.light', v);
    } else {
      updateColor('textLight', v);
    }
  }, [colors.text, updateColor]);

  const handleAccentChange = useCallback((v) => {
    updateColor('accent', v);
  }, [updateColor]);

  // Count active colors for badge
  const colorCount = [primaryColor, colors.secondary, accentColor, bgColor, textColor, textSecondary, textLight, surfaceColor, borderColor]
    .filter(Boolean).length;

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full overflow-y-auto">
      {/* Highlight CSS injection */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
        .editable-renderer[data-highlight-color="primary"] [class*="bg-gray-900"],
        .editable-renderer[data-highlight-color="primary"] [class*="bg-gray-950"],
        .editable-renderer[data-highlight-color="primary"] [class*="bg-black"],
        .editable-renderer[data-highlight-color="primary"] button[class*="bg-"] {
          outline: 3px solid #3b82f6 !important; outline-offset: 2px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="background"] {
          outline: 3px solid #3b82f6 !important; outline-offset: -3px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="text"] p,
        .editable-renderer[data-highlight-color="text"] span:not([class*="bg-"]),
        .editable-renderer[data-highlight-color="text"] h1,
        .editable-renderer[data-highlight-color="text"] h2,
        .editable-renderer[data-highlight-color="text"] h3 {
          outline: 2px solid #3b82f680 !important; outline-offset: 1px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="surface"] [class*="bg-white"],
        .editable-renderer[data-highlight-color="surface"] [class*="rounded"] {
          outline: 3px solid #3b82f6 !important; outline-offset: 2px; transition: outline 0.2s;
        }
        .editable-renderer[data-highlight-color="border"] [class*="border-"] {
          border-color: #3b82f6 !important; border-width: 2px !important; transition: border 0.2s;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <h3 className="text-sm font-bold text-gray-800">Theme Designer</h3>
          {showApplied && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full animate-fadeIn">
              <Check className="w-3 h-3" /> Applied
            </span>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 hover:bg-white/80 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Colors */}
      <CollapsibleSection title="Brand Colors" icon={Palette} badge={`${colorCount} active`}>
        <ColorInput
          label={COLOR_DESCRIPTIONS.primary.label}
          hint={COLOR_DESCRIPTIONS.primary.hint}
          value={primaryColor}
          colorKey="primary"
          onChange={(v) => updateColor('primary', v)}
          onHover={setHighlightedColor}
          onLeave={() => setHighlightedColor(null)}
        />
        {colors.secondary !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.secondary.label}
            hint={COLOR_DESCRIPTIONS.secondary.hint}
            value={colors.secondary}
            colorKey="secondary"
            onChange={(v) => updateColor('secondary', v)}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {accentColor !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.accent.label}
            hint={COLOR_DESCRIPTIONS.accent.hint}
            value={accentColor}
            colorKey="accent"
            onChange={handleAccentChange}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        <ColorInput
          label={COLOR_DESCRIPTIONS.background.label}
          hint={COLOR_DESCRIPTIONS.background.hint}
          value={bgColor}
          colorKey="background"
          onChange={(v) => updateColor('background', v)}
          onHover={setHighlightedColor}
          onLeave={() => setHighlightedColor(null)}
        />
        <ColorInput
          label={COLOR_DESCRIPTIONS.text.label}
          hint={COLOR_DESCRIPTIONS.text.hint}
          value={textColor}
          colorKey="text"
          onChange={handleTextChange}
          onHover={setHighlightedColor}
          onLeave={() => setHighlightedColor(null)}
        />
        {textSecondary !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.textSecondary.label}
            hint={COLOR_DESCRIPTIONS.textSecondary.hint}
            value={textSecondary}
            colorKey="textSecondary"
            onChange={handleTextSecondaryChange}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {textLight !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.textLight.label}
            hint={COLOR_DESCRIPTIONS.textLight.hint}
            value={textLight}
            colorKey="textLight"
            onChange={handleTextLightChange}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {surfaceColor !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.surface.label}
            hint={COLOR_DESCRIPTIONS.surface.hint}
            value={surfaceColor}
            colorKey="surface"
            onChange={(v) => updateColor('surface', v)}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
        {borderColor !== undefined && (
          <ColorInput
            label={COLOR_DESCRIPTIONS.border.label}
            hint={COLOR_DESCRIPTIONS.border.hint}
            value={borderColor}
            colorKey="border"
            onChange={(v) => updateColor('border', v)}
            onHover={setHighlightedColor}
            onLeave={() => setHighlightedColor(null)}
          />
        )}
      </CollapsibleSection>

      {/* Typography */}
      <CollapsibleSection title="Fonts & Typography" icon={Type}>
        <FontSelect
          label="Heading Font"
          hint="Used for all titles, section headings, and hero text"
          value={typography.heading}
          onChange={(v) => updateFont('heading', v)}
        />
        <div className="my-2 border-t border-gray-100" />
        <FontSelect
          label="Body Font"
          hint="Used for paragraphs, descriptions, buttons, and labels"
          value={typography.body}
          onChange={(v) => updateFont('body', v)}
        />
      </CollapsibleSection>
    </div>
  );
};

export default ThemePanel;
